/**
 * Swipe-to-dismiss mixin for mobile bottom sheets (lp-drawer / lp-modal).
 *
 * On phones (<= 640px, where the panel is rendered as a bottom sheet) the user
 * can drag the sheet down with one finger. The panel follows the finger and the
 * overlay dims proportionally; releasing past the threshold animates the sheet
 * the rest of the way out and then emits `close`, otherwise it springs back.
 *
 * The host component must expose these refs:
 *   - $refs.panel   the sheet element that slides
 *   - $refs.overlay the dimmed backdrop (optional)
 *   - $refs.body    the scrollable content (optional — drag only engages when
 *                   this is scrolled to the top so it doesn't fight scrolling)
 * and a `close()` method that emits the close event.
 */
const MOBILE_QUERY = '(max-width: 640px)';

// How far the finger must travel before the gesture is classified. Below this
// the direction is just jitter: a scroll flick routinely reports a pixel or two
// downward before it goes up, and treating that as a dismiss swallows the whole
// gesture (every touchmove is preventDefault-ed) so the sheet never scrolls.
const ENGAGE_PX = 8;

export default {
  mounted() {
    this._swipeMql = window.matchMedia(MOBILE_QUERY);
    this._onSwipeStart = this.__swipeStart.bind(this);
    this._onSwipeMove = this.__swipeMove.bind(this);
    this._onSwipeEnd = this.__swipeEnd.bind(this);
    // Attach after the DOM settles — the panel lives inside <Teleport>, which
    // relocates it to <body>, so wait a tick before resolving the ref.
    this.$nextTick(() => {
      const panel = this.$refs.panel;
      if (!panel) return;
      panel.addEventListener('touchstart', this._onSwipeStart, { passive: true });
      panel.addEventListener('touchmove', this._onSwipeMove, { passive: false });
      panel.addEventListener('touchend', this._onSwipeEnd, { passive: true });
      panel.addEventListener('touchcancel', this._onSwipeEnd, { passive: true });
    });
  },
  beforeDestroy() {
    const panel = this.$refs.panel;
    if (!panel) return;
    panel.removeEventListener('touchstart', this._onSwipeStart);
    panel.removeEventListener('touchmove', this._onSwipeMove);
    panel.removeEventListener('touchend', this._onSwipeEnd);
    panel.removeEventListener('touchcancel', this._onSwipeEnd);
  },
  methods: {
    __swipeStart(e) {
      if (!this._swipeMql || !this._swipeMql.matches) return;
      if (!e.touches || e.touches.length !== 1) {
        this._swipe = null;
        return;
      }
      const t = e.touches[0];
      // Whether the gesture began on the scrollable content vs. the sheet's
      // chrome (header / drag-handle). Dragging the chrome dismisses from any
      // scroll position; dragging the content only dismisses when it's at the
      // top, so a scrolled sheet can still be closed without scrolling back up.
      const scroller = this.$refs.body;
      const fromScroller = !!(scroller && e.target && scroller.contains(e.target));
      this._swipe = {
        startY: t.clientY,
        startX: t.clientX,
        delta: 0,
        active: false,
        // Set once the gesture is classified as a scroll: the sheet then keeps
        // its hands off for the rest of the touch, so a mid-flick change of
        // direction can't yank a scrolling list into a dismiss.
        rejected: false,
        fromScroller,
      };
    },
    __swipeMove(e) {
      const s = this._swipe;
      if (!s || s.rejected) return;
      const t = e.touches[0];
      const dy = t.clientY - s.startY;
      const dx = t.clientX - s.startX;

      if (!s.active) {
        // Undecided: let the finger travel far enough to tell a downward drag
        // from the first pixels of a scroll.
        if (Math.abs(dy) < ENGAGE_PX && Math.abs(dx) < ENGAGE_PX) return;
        // Upward or sideways — a scroll, not a dismiss.
        if (dy <= 0 || Math.abs(dx) > Math.abs(dy)) {
          s.rejected = true;
          return;
        }
        // For drags that started on the scrollable content, only engage when
        // it's at the top so native scrolling still works. Drags that started
        // on the chrome (header / handle) always engage — this is what lets a
        // scrolled sheet be dismissed without first scrolling back to the top.
        if (s.fromScroller) {
          const scroller = this.$refs.body;
          if (scroller && scroller.scrollTop > 0) {
            s.rejected = true;
            return;
          }
        }
        s.active = true;
        this.$refs.panel.style.transition = 'none';
        const overlay = this.$refs.overlay;
        if (overlay) overlay.style.transition = 'none';
      }

      s.delta = Math.max(0, dy);
      e.preventDefault();
      const panel = this.$refs.panel;
      panel.style.transform = `translateY(${s.delta}px)`;
      const overlay = this.$refs.overlay;
      if (overlay) {
        const h = panel.offsetHeight || 1;
        overlay.style.opacity = String(Math.max(0, 1 - s.delta / h));
      }
    },
    __swipeEnd() {
      const s = this._swipe;
      this._swipe = null;
      if (!s || !s.active) return;

      const panel = this.$refs.panel;
      const overlay = this.$refs.overlay;
      const h = panel.offsetHeight || 1;
      const threshold = Math.min(140, h * 0.28);

      if (s.delta > threshold) {
        // Fling / drag past threshold: finish the exit, then close.
        panel.style.transition = 'transform 0.24s cubic-bezier(0.3, 0, 0.4, 1)';
        panel.style.transform = 'translateY(100%)';
        if (overlay) {
          overlay.style.transition = 'opacity 0.24s ease-out';
          overlay.style.opacity = '0';
        }
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          panel.removeEventListener('transitionend', finish);
          this.close();
        };
        panel.addEventListener('transitionend', finish);
        setTimeout(finish, 320);
      } else {
        // Not far enough: spring back to the resting position.
        const reset = () => {
          panel.removeEventListener('transitionend', reset);
          panel.style.transition = '';
          panel.style.transform = '';
          if (overlay) overlay.style.transition = '';
        };

        // A gesture can engage and still leave the sheet at rest (the finger
        // went down, then straight back up). There is nothing to animate then,
        // and waiting on a `transitionend` that never fires would strand the
        // inline transform on the panel — so clean up right away instead.
        if (s.delta <= 0) {
          reset();
          if (overlay) overlay.style.opacity = '';
          return;
        }

        panel.style.transition = 'transform 0.22s cubic-bezier(0.2, 0.7, 0.2, 1)';
        panel.style.transform = 'translateY(0)';
        if (overlay) {
          overlay.style.transition = 'opacity 0.2s ease-out';
          overlay.style.opacity = '';
        }
        panel.addEventListener('transitionend', reset);
        setTimeout(reset, 300); // belt and braces if the transition is dropped
      }
    },
  },
};
